import { useContext, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import { useBooleanFlags } from '@/domains/core/flags/flags.hooks';
import { Button } from '@/domains/core/tamagoshiTailwind/components/Button/Button';
import { Label } from '@/domains/core/tamagoshiTailwind/components/Label/Label';
import { P } from '@/domains/core/tamagoshiTailwind/components/Typography/Typography';
import { CheckCircleIcon } from '@/domains/core/tamagoshiTailwind/icons/CheckCircleIcon';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import { CouponModal } from '@/domains/loyalty/components/Coupon/components/CouponModal/CouponModal';
import { CouponContext } from '@/domains/loyalty/components/Coupon/CouponContext';
import { genericCouponFooter } from '@/domains/loyalty/components/Coupon/translations';
import {
  codeCopied,
  conditions,
  copyCode,
} from '@/domains/loyalty/modules/HighlightedCoupon/translations';
import { GTM_CLICK_ON_COUPON_CODE } from '@/domains/productDiscovery/CommercialAnimation/services/tracking/clickOnCouponCode';
import { StringAbTests } from '@/domains/productDiscovery/ProductPage/constants';

const COPY_CONFIRMATION_DURATION_MS = 2000;

interface Props {
  label: string;
  title: string;
  description: string;
}

export const HighlightedCoupon: React.FC<Props> = ({
  label,
  title,
  description,
}) => {
  const { code, context } = useContext(CouponContext);
  const [copied, setCopied] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isProductPageRevampEnabled] = useBooleanFlags([
    StringAbTests.ENABLE_PRODUCT_PAGE_REVAMP,
  ]);

  if (!code) return null;

  const handleCopy = () => {
    Gtm.push(GTM_CLICK_ON_COUPON_CODE(context));
    setCopied(true);
    navigator.clipboard.writeText(code);
    setTimeout(() => {
      setCopied(false);
    }, COPY_CONFIRMATION_DURATION_MS);
  };

  const handleTermsLink = () => {
    Gtm.push({
      event: 'interaction_click on a link',
      amp_event_name: 'Click on a link',
      event_trigger: context,
      click_element: genericCouponFooter,
    });
    setIsModalVisible(true);
  };

  return (
    <>
      <div
        className={twJoin(
          `flex h-full w-full flex-col rounded-container bg-offer-primary p-m
          text-foundation-primary`,
          isProductPageRevampEnabled ? 'gap-s' : 'gap-m',
        )}
      >
        <div
          className={twJoin(
            'flex flex-col items-start',
            isProductPageRevampEnabled ? 'gap-xs' : 'gap-s',
          )}
        >
          <Label
            variant={
              isProductPageRevampEnabled
                ? 'FoundationInverted'
                : 'LoyaltyPrimary'
            }
            text={label}
          />
          <div className="flex flex-col">
            {!isProductPageRevampEnabled && (
              <P scale="body2" weight="bold">
                {title}
              </P>
            )}
            <P
              scale="body2"
              weight={isProductPageRevampEnabled ? 'semibold' : 'regular'}
            >
              {description}
            </P>
          </div>
        </div>
        <div
          className={twJoin(
            'flex',
            isProductPageRevampEnabled
              ? 'mt-auto flex-col-reverse gap-s'
              : 'flex-col gap-m',
          )}
        >
          <Button
            variant="tertiary"
            text={copied ? codeCopied : (copyCode.format({ code }) as string)}
            icon={copied ? CheckCircleIcon : undefined}
            onClick={handleCopy}
            size={isProductPageRevampEnabled ? 'M' : 'L'}
          />
          <Button
            onClick={handleTermsLink}
            variant="link"
            text={isProductPageRevampEnabled ? conditions : genericCouponFooter}
            size="M"
            className="self-start"
          />
        </div>
      </div>
      {isModalVisible && (
        <CouponModal handleClose={() => setIsModalVisible(false)} />
      )}
    </>
  );
};

HighlightedCoupon.displayName = 'HighlightedCoupon';
